import { SyntheticEvent, useEffect, useState } from 'react';

import { TParsedProductInstance } from '@lib/core/products/types';
import { MIXPANEL_PRODUCT_RATE_VALUES } from '@lib/core/users/slices/productRate';
import MixpanelTracker from '@lib/tools/dat/mixpanel';
import { MixpanelPositionContext } from '@lib/tools/dat/mixpanel/consts';
import { localeCommon } from '@lib/tools/locale/source/web/common';
import { LocaleUtils } from '@lib/tools/locale/utils';
import { useEqualElementsHeight } from '@lib/tools/views/hooks';

import Button from '@components/web/src/atoms/Buttons/Button';
import { Flexbox } from '@components/web/src/foundations/Flexbox/Flexbox';
import { Text } from '@components/web/src/foundations/Text/Text';
import * as S from '@components/web/src/organisms/ProductCards/ProductCard/components/styles';
import { COLORS } from '@components/web/src/styles';

/** Props passed when using IntentGate (suggestion page only). */
export interface IntentGateProps {
  handlePostFeedbackDispatched: (value: boolean, identifier: string) => void;
  /** Used to navigate to the product page on "Yes" feedback. */
  navigateToProductUrl: (url: string) => void;
}

interface Props extends IntentGateProps {
  parsedProductInstanceData: TParsedProductInstance;
  isActiveCard?: boolean;
  mixpanelPositionContext?: MixpanelPositionContext;
  mixpanelIndex?: number;
}

export const IntentGate = ({
  parsedProductInstanceData,
  mixpanelIndex,
  mixpanelPositionContext,
  handlePostFeedbackDispatched,
  isActiveCard,
  navigateToProductUrl,
}: Props) => {
  const {
    suggestionTitle,
    suggestionYes,
    suggestionNo,
    suggestionSubmit,
    suggestionCancel,
    suggestionTextArea,
    thanksForFeedback,
    didYouLikeIt,
  } = localeCommon.productCard;
  const textAreaPlaceholderText =
    LocaleUtils?.publishedTerms[suggestionTextArea?.id] || suggestionTextArea?.defaultMessage;

  const {
    isCommentInList,
    isCommentUpdateLoading,
    handleSubmitComment,
    productId,
    productProducerUrl,
    isDisableUserPersistenceAddon,
  } = parsedProductInstanceData;
  const titleText = isDisableUserPersistenceAddon ? didYouLikeIt : suggestionTitle;

  useEqualElementsHeight(['feedback-block']);

  const [comment, setComment] = useState('');
  const [isFeedbackDone, setIsFeedbackDone] = useState(false);
  const [isCommentDone, setIsCommentDone] = useState(isCommentInList);
  const [isOpenedIntentGate, setIsOpenedIntentGate] = useState(false);

  useEffect(() => {
    if (isCommentInList) {
      setIsFeedbackDone(true);
    }
  }, []);

  useEffect(() => {
    if (isOpenedIntentGate && !isActiveCard) {
      setIsFeedbackDone(true);
      setIsOpenedIntentGate(false);
    }
  }, [isOpenedIntentGate, isActiveCard]);

  const isSubmitDisabled = comment.trim().length === 0 || isCommentUpdateLoading;

  const handleAreaClick = (e: SyntheticEvent) => e.stopPropagation();

  const handleSubmit = async (e: SyntheticEvent) => {
    handleAreaClick(e);
    if (isCommentUpdateLoading) return;
    await handleSubmitComment({ comment: comment.trim(), parsedProductInstanceData });
    setIsOpenedIntentGate(false);
    setIsCommentDone(true);
    setIsFeedbackDone(true);
  };

  const handleFeedbackYesClick = async (e: SyntheticEvent) => {
    handleAreaClick(e);
    handlePostFeedbackDispatched(true, productId);
    setIsFeedbackDone(true);
    await MixpanelTracker.events.productFeedbackAsync(parsedProductInstanceData, MIXPANEL_PRODUCT_RATE_VALUES['5']);
    if (productProducerUrl) {
      await MixpanelTracker.events.contactTheProductProducerAsync(
        parsedProductInstanceData,
        mixpanelIndex,
        mixpanelPositionContext,
      );
      navigateToProductUrl(productProducerUrl);
    }
  };

  const handleFeedbackNoClick = (e: SyntheticEvent) => {
    handleAreaClick(e);
    if (isOpenedIntentGate) return;

    setIsOpenedIntentGate(true);
    MixpanelTracker.events.productFeedback(parsedProductInstanceData, MIXPANEL_PRODUCT_RATE_VALUES['0']);
    handlePostFeedbackDispatched(false, productId);
  };

  return (
    <S.IntentGateContainer isFullWidth direction="column" gap={isOpenedIntentGate ? 12 : 0} onClick={handleAreaClick}>
      <Flexbox isFullWidth align="center" className="feedback-block" direction="column" gap={13} justify="center">
        <Text
          color={COLORS.base.black}
          size="body2"
          text={isFeedbackDone ? thanksForFeedback : titleText}
          textAlign="center"
          weight="semibold"
        />
        {!isFeedbackDone && isActiveCard && (
          <Flexbox isFullWidth gap={10}>
            {!isOpenedIntentGate && (
              <Button
                isFullWidth
                fontSize="body1"
                text={suggestionYes}
                textWeight="medium"
                onClick={e => handleFeedbackYesClick(e)}
              />
            )}
            <S.SecondaryButton
              isFullWidth
              fontSize="body1"
              text={suggestionNo}
              textWeight="medium"
              onClick={e => handleFeedbackNoClick(e)}
            />
          </Flexbox>
        )}
      </Flexbox>
      {!isCommentDone && isActiveCard && (
        <S.CommentWrapper $open={isOpenedIntentGate}>
          <div>
            <Flexbox isFullWidth direction="column" gap={12} onClick={handleAreaClick}>
              <S.TextArea
                placeholder={textAreaPlaceholderText}
                value={comment}
                onChange={e => setComment(e.target.value)}
              />
              <Button
                disabled={isSubmitDisabled}
                fontSize="body1"
                text={suggestionSubmit}
                textWeight="medium"
                onClick={handleSubmit}
              />
              <Button
                fontSize="body1"
                text={suggestionCancel}
                textWeight="medium"
                variant="link"
                onClick={e => {
                  handleAreaClick(e);
                  setIsOpenedIntentGate(false);
                  setIsFeedbackDone(true);
                }}
              />
            </Flexbox>
          </div>
        </S.CommentWrapper>
      )}
    </S.IntentGateContainer>
  );
};
